import type { Address, ContactPoint, HumanName, Patient } from '@medplum/fhirtypes';
import type { Client } from '../pages/caseload/data';

/**
 * Map app Client to FHIR Patient for Medplum components.
 */
export function clientToPatient(client: Client): Patient {
  const names: HumanName[] = [];
  if (client.preferredFirstName || client.preferredLastName) {
    names.push({
      use: 'nickname',
      given: client.preferredFirstName ? [client.preferredFirstName] : undefined,
      family: client.preferredLastName,
      text: [client.preferredFirstName, client.preferredLastName].filter(Boolean).join(' '),
    });
  }
  names.push({
    use: 'official',
    given: client.firstName ? [client.firstName] : undefined,
    family: client.lastName,
    text: client.name,
  });

  const patient: Patient = {
    resourceType: 'Patient',
    id: client.id,
    name: names,
    birthDate: client.dateOfBirth,
    gender: undefined,
    address:
      client.address || client.city || client.state || client.zipCode
        ? [
          {
            line: client.address ? [client.address] : undefined,
            city: client.city,
            state: client.state,
            postalCode: client.zipCode,
          },
        ]
        : undefined,
    telecom: [],
  };

  if (client.phone) {
    patient.telecom!.push({ system: 'phone', value: client.phone, use: 'work' });
  }
  if (client.email) {
    patient.telecom!.push({ system: 'email', value: client.email, use: 'work' });
  }

  return patient;
}

export function getClientAddress(client: Client): Address | undefined {
  if (!client.address && !client.city && !client.state && !client.zipCode) return undefined;
  return {
    line: client.address ? [client.address] : undefined,
    city: client.city,
    state: client.state,
    postalCode: client.zipCode,
  };
}

export function getClientPhone(client: Client): ContactPoint | undefined {
  if (!client.phone) return undefined;
  return { system: 'phone', value: client.phone, use: 'work' };
}

export function getClientEmail(client: Client): ContactPoint | undefined {
  if (!client.email) return undefined;
  return { system: 'email', value: client.email, use: 'work' };
}

export function getClientOfficialName(client: Client): HumanName | undefined {
  if (!client.firstName && !client.lastName) return undefined;
  return {
    use: 'official',
    given: client.firstName ? [client.firstName] : undefined,
    family: client.lastName,
    text: client.name,
  };
}

export function getClientPreferredName(client: Client): HumanName | undefined {
  if (!client.preferredFirstName && !client.preferredLastName) return undefined;
  return {
    use: 'nickname',
    given: client.preferredFirstName ? [client.preferredFirstName] : undefined,
    family: client.preferredLastName,
    text: [client.preferredFirstName, client.preferredLastName].filter(Boolean).join(' '),
  };
}
